import type { BasketItem, Product, ProductSize } from '../types/order';
import {
  defaultSize,
  feeUnitGross,
  isFeeProduct,
  isSizeSellable,
  sizeChannelPrice,
} from './pricing';

/**
 * Γραμμή καλαθιού από σκανάρισμα barcode (TakeAway / DeliveryWithMap).
 *
 * ΓΙΑΤΙ: το data.product του barcode-lookup δεν έχει τιμή — η τιμή ζει στο μέγεθος (Φ5).
 * Χωρίς μέγεθος η γραμμή θα ήταν 0 € και το κατώτατο του server (αποφάσεις 12, F1-7) θα την
 * έκοβε με 403. Εδώ:
 *  - κανονικό barcode → το προεπιλεγμένο μέγεθος (default_size_id του server, αλλιώς §3.3) και
 *    η τιμή του ανά κανάλι, ίδια με το πλακίδιο και το κατώτατο·
 *  - ζυγιζόμενο → τα ποσά ανά κανάλι ΟΠΩΣ τα έδωσε ο server, ΧΩΡΙΣ επανυπολογισμό: ο server
 *    στρογγυλεύει το σύνολο με τον ίδιο τρόπο που ελέγχει το κατώτατο (weightedLineCents)·
 *  - τέλος → ποσό state_fees, χωρίς μέγεθος·
 *  - ανοιχτή τιμή, ή ζυγιζόμενο σκαναρισμένο με ΚΑΝΟΝΙΚΟ barcode → χωρίς μέγεθος, 0 €, όπως ως
 *    τώρα (ο κατάλογος δεν έχει τιμή γι' αυτά· με το προεπιλεγμένο θα χρέωνε άλλο ποσό).
 *
 * Καθαρή συνάρτηση, για να δοκιμάζεται χωρίς οθόνη· οι οθόνες κάνουν μόνο το setBasket.
 */

export type BarcodeChannel = 'take_away' | 'delivery';

/** Το data.product του barcode-lookup: γραμμή products + το προεπιλεγμένο μέγεθος του server. */
export type BarcodeProduct = Product & {
  default_size_id?: number | null;
};

type NumLike = number | string | null | undefined;
type ByChannel = Partial<Record<'take_away' | 'delivery' | 'dine_in', NumLike>>;

/** Απάντηση του GET /api/products/barcode-lookup. */
export interface BarcodeLookupData {
  product: BarcodeProduct;
  /** Τα ΠΩΛΗΣΙΜΑ μεγέθη, ταξινομημένα. Εκτός product: δες το σχόλιο στο routes/products.js. */
  sizes?: ProductSize[];
  scan: {
    type: 'standard' | 'weighted';
    weight?: number | string;
    weightGrams?: number;
    size_id?: number | null;
    pricePerKg?: NumLike;
    totalPrice?: NumLike;
    pricePerKgByChannel?: ByChannel;
    totalPriceByChannel?: ByChannel;
  };
}

export type BarcodeLine =
  | { kind: 'error'; reason: 'unavailable' | 'no_price' }
  | { kind: 'line'; product: Product; size: ProductSize | null; unitPrice: number }
  | { kind: 'weighted'; product: Product; size: ProductSize | null; perKg: number; weight: number; total: number };

const num = (v: NumLike): number => {
  const n = Number(v);
  return v === null || v === undefined || (v as unknown) === '' || !Number.isFinite(n) ? NaN : n;
};

/** Τιμή ανά κανάλι του server· αλλιώς το πεδίο take away (ταμείο με παλιό backend). */
const channelValue = (byChannel: ByChannel | undefined, channel: BarcodeChannel, fallback: NumLike): number => {
  const v = byChannel?.[channel];
  return num(v === null || v === undefined ? fallback : v);
};

export function resolveBarcodeLine({ data, catalog, channel }: {
  data: BarcodeLookupData;
  catalog: Product[];
  channel: BarcodeChannel;
}): BarcodeLine {
  const raw = data.product;
  // Ίδιος έλεγχος με πριν (!is_available): NULL/απόν μετρά ως μη διαθέσιμο.
  if (!raw.is_available) return { kind: 'error', reason: 'unavailable' };

  // Το προϊόν του καταλόγου POS φέρνει fee_unit_gross και τα μεγέθη όπως τα βλέπει η οθόνη·
  // αν λείπει (π.χ. κρυφή κατηγορία), από την απάντηση με ΜΟΝΟ τα πωλήσιμα μεγέθη.
  const product: Product = catalog.find(p => Number(p.id) === Number(raw.id))
    ?? { ...raw, sizes: (data.sizes ?? raw.sizes ?? []).filter(isSizeSellable) };

  if (data.scan?.type === 'weighted') {
    const perKg = channelValue(data.scan.pricePerKgByChannel, channel, data.scan.pricePerKg);
    const total = channelValue(data.scan.totalPriceByChannel, channel, data.scan.totalPrice);
    const weight = num(data.scan.weight);
    if (!(perKg > 0) || !Number.isFinite(total) || !Number.isFinite(weight)) {
      return { kind: 'error', reason: 'no_price' };
    }
    // Το μέγεθος το διαλέγει ο server· πάει ως selectedSize ώστε το κατώτατο (μέγεθος × βάρος)
    // και η απόδοση υλικών να ξέρουν ποιο πουλήθηκε.
    const sizeId = data.scan.size_id;
    const size = sizeId === null || sizeId === undefined
      ? null
      : (product.sizes ?? []).find(s => Number(s.id) === Number(sizeId)) ?? null;
    return { kind: 'weighted', product, size, perKg, weight, total };
  }

  // Τέλος: ποσό state_fees (απόφαση 1), ΠΑΝΤΑ χωρίς μέγεθος· χωρίς ποσό καμία γραμμή (απόφαση 33).
  if (isFeeProduct(product)) {
    const unit = feeUnitGross(product);
    return unit === null
      ? { kind: 'error', reason: 'no_price' }
      : { kind: 'line', product, size: null, unitPrice: unit };
  }

  // Ανοιχτή τιμή, ή ζυγιζόμενο με κανονικό barcode: η γραμμή όπως ως τώρα — το data.product
  // ΧΩΡΙΣ μεγέθη (ώστε η επεξεργασία στο καλάθι να μην την ξανατιμολογήσει σε μέγεθος), 0 €.
  if (product.is_open_price || raw.is_open_price || product.is_weight_variable || raw.is_weight_variable) {
    return { kind: 'line', product: { ...raw, sizes: undefined }, size: null, unitPrice: 0 };
  }

  const sizes = product.sizes ?? [];
  const size = sizes.find(s => Number(s.id) === Number(raw.default_size_id)) ?? defaultSize(sizes);
  // Κανένα πωλήσιμο μέγεθος: ο server στέλνει [OPS-ALERT] NO_SIZE_PRODUCT· εδώ καμία γραμμή 0 €.
  if (!size) return { kind: 'error', reason: 'unavailable' };
  return { kind: 'line', product, size, unitPrice: sizeChannelPrice(size, channel) };
}

/** Τιμή ευρώ → ακέραια λεπτά· μη αριθμητικό → NaN (ποτέ δεν ταιριάζει). */
const cents = (v: NumLike): number => {
  const n = num(v);
  return Number.isFinite(n) ? Math.round(n * 100) : NaN;
};

/**
 * Η γραμμή του καλαθιού όπου το δεύτερο σκανάρισμα γίνεται ποσότητα +1 — ΑΥΣΤΗΡΟΣ κανόνας,
 * απόφαση F1-W4-3 (επιβεβαιωμένη από την F1-W5-13). Συγχώνευση ΜΟΝΟ όταν η υπάρχουσα γραμμή:
 *  - είναι το ίδιο προϊόν ΚΑΙ το ίδιο μέγεθος (ή και οι δύο χωρίς μέγεθος),
 *  - δεν έχει βάρος, έξτρα, αφαιρέσεις, επιλογές ή σημείωση,
 *  - δεν έχει έκπτωση γραμμής,
 *  - έχει ΙΔΙΑ τιμή μονάδας με το barcode (σε ακέραια λεπτά),
 *  - ΔΕΝ είναι γραμμή επανάληψης που κρατά το ιστορικό της τέλος (keepHistoricalFee).
 * Αλλιώς undefined → οι οθόνες βάζουν ΝΕΑ γραμμή στην τιμή του barcode.
 *
 * Επανάληψη (αποφάσεις F1-W6-2/F1-W6-3): οι γραμμές της ΔΕΝ γεννούν αυτόματο τέλος 16· ένα νέο
 * σκαναρισμένο τεμάχιο είναι «είδος που προσθέτει ο ταμίας» και ΠΑΙΡΝΕΙ τέλος. Αν γινόταν +1 σε
 * γραμμή επανάληψης με ίδια τιμή (ο κανόνας στις παραγγελίες ταμείου: ιστορική = τρέχουσα), θα
 * έμενε χωρίς τέλος — υπο-χρέωση. Γίνεται νέα κανονική γραμμή· ίδια τιμή μονάδας, άρα το ποσό του
 * είδους δεν αλλάζει, και το τέλος του μπαίνει στην αυτόματη γραμμή.
 *
 * ΓΙΑΤΙ: το σκαναρισμένο τεμάχιο χρεώνεται ΠΑΝΤΑ την τιμή του barcode. Με τον παλιό χαλαρό
 * κανόνα (FE-3, 63444a8: ίδιο προϊόν αρκούσε) ένα «ΤΟΣΤ» σκαναρισμένο πάνω σε γραμμή «ΤΟΣΤ XL»
 * 2,50 χρεωνόταν σιωπηλά ως XL (5,00 αντί για 2,50 + 2,00), και πάνω σε γραμμή με έκπτωση 10%
 * έπαιρνε κι αυτό την έκπτωση. Η ίδια τιμή μονάδας πιάνει και ό,τι άλλο αλλάζει την τιμή της
 * γραμμής χωρίς να φαίνεται στα πεδία (ανοιχτή τιμή, προωθητική ενέργεια, ιστορική τιμή
 * επανάληψης). Η σημείωση μπλοκάρει κι αυτή: αφορά το συγκεκριμένο τεμάχιο (π.χ. «χωρίς
 * ζάχαρη»), όχι ένα καθαρό σκαναρισμένο — και με ίδια τιμή μονάδας η νέα γραμμή δεν αλλάζει ποσό.
 *
 * Τιμή μονάδας της γραμμής: TakeAway = totalPrice (ήδη με έκπτωση/επιλογές), DeliveryWithMap =
 * price (ίδιο με totalPrice· το price είναι αυτό που αθροίζει η οθόνη).
 *
 * ΕΠΙΠΤΩΣΗ (διόρθωση της εκτίμησης «0 προϊόντα με barcode» του 28408ab/F1-W4-3): στην παραγωγή
 * το κατ. 34 «ARTISAN BAKERY» (από 28/9) έχει 110 προϊόντα με barcode (109 διαθέσιμα, 29
 * ζυγιζόμενα, όλα με τιμή καναλιού, κανένα με μέγεθος). Ως τις 29/9 δεν είχε καμία παραγγελία (read-only μέτρηση
 * ελέγχου). Εκεί ο κανόνας αλλάζει ποσό σε σχέση με το phase1: δεύτερο σκανάρισμα πάνω σε
 * γραμμή με έκπτωση, επιλογή με προσαύξηση ή προωθητική ενέργεια γίνεται νέα γραμμή στην τιμή
 * του barcode. Η αλλαγή καλύπτεται από την F1-W4-3 (decided), θέλει όμως επιβεβαίωση του
 * ιδιοκτήτη για το κατ. 34 πριν το deploy στην παραγωγή.
 */
export function findBarcodeMergeLine(
  basket: BasketItem[],
  line: { product: Product; size: ProductSize | null; unitPrice: number }
): BasketItem | undefined {
  const lineSizeId = line.size ? Number(line.size.id) : null;
  const lineCents = cents(line.unitPrice);
  return basket.find(item => {
    const itemSizeId = item.selectedSize ? Number(item.selectedSize.id) : null;
    const itemUnit = item.price ?? item.totalPrice;
    return (
      Number(item.product.id) === Number(line.product.id) &&
      itemSizeId === lineSizeId &&
      !item.weight &&
      (!item.removedIngredients || item.removedIngredients.length === 0) &&
      (!item.addedExtras || item.addedExtras.length === 0) &&
      (!item.selected_options || item.selected_options.length === 0) &&
      !(item.specialInstructions && item.specialInstructions.trim()) &&
      !(item.notes && item.notes.trim()) &&
      !(Number(item.discount) > 0) &&
      !item.keepHistoricalFee &&
      cents(itemUnit) === lineCents
    );
  });
}
